// Ubicación en el repo: src/components/HolidaysSettings.jsx
// "Feriados y cierres" — sub-pestaña de Configuración (migración 0043,
// 23-ago-2026, ver DECISIONS.md). Lista GLOBAL de fechas bloqueadas para
// el auto-agendamiento público, con excepciones puntuales por sucursal
// (una sucursal que sí atiende ese día pese al cierre general). Solo
// Admin — RLS (`clinic_holidays_*`) ya lo exige del lado del servidor,
// esta pantalla también se gatea del lado del cliente para no mostrar un
// formulario que el backend igual va a rechazar.
import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  listHolidays,
  createHoliday,
  deleteHoliday,
  addHolidayException,
  removeHolidayException,
} from '../lib/holidays';
import { listActiveBranches } from '../lib/branches';
import { formatDateLong } from '../lib/format';
import Spinner from './Spinner';

export default function HolidaysSettings({ isAdmin, actorId }) {
  const [holidays, setHolidays] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newDate, setNewDate] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [busyException, setBusyException] = useState(null); // `${holidayId}:${branchId}`

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [h, b] = await Promise.all([listHolidays(), listActiveBranches()]);
      setHolidays(h);
      setBranches(b);
    } catch (err) {
      setError(err.message || 'No se pudo cargar los feriados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin, refresh]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!newDate || !newLabel.trim()) return;
    setSaving(true);
    setError('');
    try {
      await createHoliday({ date: newDate, label: newLabel }, actorId);
      setNewDate('');
      setNewLabel('');
      await refresh();
    } catch (err) {
      if (err.code === '23505') {
        setError('Ya existe un feriado cargado en esa fecha.');
      } else {
        setError(err.message || 'No se pudo agregar el feriado.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    const ok = window.confirm(
      '¿Eliminar este feriado? Vuelve a quedar disponible para agendar en todas las sucursales.'
    );
    if (!ok) return;
    setError('');
    try {
      await deleteHoliday(id);
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el feriado.');
    }
  }

  async function toggleException(holiday, branchId, checked) {
    const key = `${holiday.id}:${branchId}`;
    setBusyException(key);
    setError('');
    try {
      if (checked) {
        await addHolidayException(holiday.id, branchId, actorId);
      } else {
        await removeHolidayException(holiday.id, branchId);
      }
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo actualizar la excepción.');
    } finally {
      setBusyException(null);
    }
  }

  if (!isAdmin) {
    return <p className="field-hint">Solo un Admin puede administrar feriados y cierres.</p>;
  }

  return (
    <div>
      <p className="field-hint" style={{ marginBottom: 16 }}>
        Estas fechas quedan bloqueadas para el auto-agendamiento público (/agendamiento-online) en{' '}
        <strong>todas</strong> las sucursales por defecto — sirve tanto para feriados como para cualquier
        otro cierre (capacitación, remodelación, etc.). Si una sucursal puntual sí atiende ese día, marcala
        como excepción en "Sucursales". No afecta el agendamiento manual desde la Agenda interna.
      </p>

      <form onSubmit={handleAdd} className="form-row" style={{ alignItems: 'flex-end', marginBottom: 20 }}>
        <label className="field">
          <span>Fecha</span>
          <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} required />
        </label>
        <label className="field">
          <span>Motivo</span>
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Ej: Fiestas Patrias"
            required
          />
        </label>
        <button type="submit" className="button-primary" disabled={saving}>
          {saving ? 'Agregando…' : '+ Agregar'}
        </button>
      </form>

      {error && <p className="auth-error">{error}</p>}

      {loading ? (
        <Spinner />
      ) : holidays.length === 0 ? (
        <p className="detail-empty">No hay feriados ni cierres cargados todavía.</p>
      ) : (
        <table className="patients-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Motivo</th>
              <th>Excepciones</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {holidays.map((h) => (
              <Fragment key={h.id}>
                <tr>
                  <td style={{ textTransform: 'capitalize' }}>{formatDateLong(h.date)}</td>
                  <td>{h.label}</td>
                  <td>
                    {h.exceptionBranchIds.length === 0
                      ? '—'
                      : `${h.exceptionBranchIds.length} sucursal${h.exceptionBranchIds.length === 1 ? '' : 'es'}`}
                  </td>
                  <td className="actions-cell">
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setExpandedId(expandedId === h.id ? null : h.id)}
                    >
                      {expandedId === h.id ? 'Ocultar' : 'Sucursales'}
                    </button>
                    <button
                      type="button"
                      className="link-button link-button--danger"
                      onClick={() => handleDelete(h.id)}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
                {expandedId === h.id && (
                  <tr>
                    <td colSpan={4}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
                        {branches.length === 0 && <span className="text-muted">No hay sucursales activas.</span>}
                        {branches.map((b) => {
                          const checked = h.exceptionBranchIds.includes(b.id);
                          const key = `${h.id}:${b.id}`;
                          return (
                            <label key={b.id} className="checkbox-inline">
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={busyException === key}
                                onChange={(e) => toggleException(h, b.id, e.target.checked)}
                              />
                              {b.name} atiende igual
                            </label>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
